import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { FormModal } from 'transfix-ui/components/Modals'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box } from 'transfix-ui/core'
import {
  ShipperApplication,
  useUpdateRequestForProposal,
} from 'apps/marketplace/apiTypes/requestForProposals'
import ShipperAutosuggest, { IShipperResult } from 'apps/marketplace/components/ShipperAutosuggest'
import { requestForProposalTypes } from './NormFileModal'
import { EditRfpSchema, IEditRfpSchema } from './schema/EditRfpSchema'

interface IEditRfpModalProps {
  rfpId: number
  salesforceRfpId: string
  rfpType: string
  shipperApplication?: ShipperApplication
  openConfirmation: boolean
  onError: (toastMessage: string) => void
  onRefetch: () => void
  onCloseAction: () => void
}

export const EditRfpModal = ({
  onCloseAction,
  onError,
  onRefetch,
  openConfirmation,
  rfpId,
  rfpType,
  salesforceRfpId,
  shipperApplication,
}: IEditRfpModalProps) => {
  const { error, loading, mutate: updateRequestForProposal } = useUpdateRequestForProposal({
    id: rfpId,
  })

  const methods = useForm<EditRfpSchema>({
    resolver: yupResolver(EditRfpSchema),
    defaultValues: {
      requestForProposalType: rfpType,
      salesforceRfpId,
      shipperApplication,
    },
  })

  const { control, formState, handleSubmit } = methods

  const parseFormParams = (formParams: EditRfpSchema) => {
    const enteredSalesForceID = formParams.salesforceRfpId
    const enteredRequestType = formParams.requestForProposalType
    const parsedFormParams: IEditRfpSchema = {
      salesforceRfpId: enteredSalesForceID?.length === 0 ? salesforceRfpId : enteredSalesForceID,
      requestForProposalType: !enteredRequestType ? rfpType : enteredRequestType,
      shipperApplicationId: formParams.shipperApplication?.id,
    }
    return parsedFormParams
  }

  const editRequestForProposal = async (formParams: EditRfpSchema) => {
    try {
      const parsedFormParams = parseFormParams(formParams)
      await updateRequestForProposal(
        {
          requestForProposal: {
            salesforceRfpId: parsedFormParams.salesforceRfpId,
            shipperApplicationId: parsedFormParams.shipperApplicationId,
            requestForProposalType: parsedFormParams.requestForProposalType,
          },
        },
        {
          pathParams: { id: rfpId },
        }
      )
      onRefetch()
      onCloseAction()
    } catch (e) {
      onError('Edit request failed')
    }
  }

  return (
    <FormModal
      size={4}
      onClose={onCloseAction}
      open={openConfirmation}
      error={error?.data ? 'Edit request failed.' : ''}
      title='Edit RFP'
      className='ph0'
      actions={[
        {
          type: 'dismiss',
          label: 'Cancel',
          dataTest: 'cancel-button',
          action: onCloseAction,
          disabled: loading,
        },
        {
          label: 'Save Changes',
          dataTest: 'edit-rfp-button',
          type: 'confirm',
          action: handleSubmit(editRequestForProposal),
          disabled: !formState.isDirty || loading,
        },
      ]}>
      {loading && (
        <Box mb={3}>
          <Spinner position='relative' />
        </Box>
      )}
      <Box mb={2}>
        <Controller
          name='shipperApplication'
          control={control}
          render={({ onChange, value }) => {
            const existingShipperHandler = (result: Partial<IShipperResult> | undefined) => {
              onChange({
                name: result?.name,
                id: result?.id,
              })
            }
            return (
              <ShipperAutosuggest
                label='Shipper Name'
                onChange={existingShipperHandler}
                value={value?.name}
                dataTest='edit-rfp-shipper'
                confirmed={false}
                defaultValue={{
                  id: shipperApplication?.id.toString() || '',
                  name: shipperApplication?.name,
                }}
                large
              />
            )
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledSelect
          control={control}
          name='requestForProposalType'
          SelectProps={{
            label: 'RFP Type',
            options: requestForProposalTypes,
            className: 'mb3',
            dataTest: 'edit-rfp-type',
            large: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='salesforceRfpId'
          TextInputProps={{
            name: 'salesforceRfpId',
            label: 'Salesforce RFP ID',
            placeholder: 'Enter Salesforce RFP ID',
            dataTest: 'edit-rfp-salesforce-id',
            defaultValue: salesforceRfpId,
          }}
        />
      </Box>
    </FormModal>
  )
}
